import type { AeroCandidateAggregatedResultsResult, AeroCandidateAllResultsResult, AeroCandidateTopResultsResult } from '../api';
import { isAeroCandidatePromotable } from './aeroCandidatePromotionEligibility';

export type AeroCandidateReviewRow = (
    | AeroCandidateAggregatedResultsResult
    | AeroCandidateAllResultsResult
    | AeroCandidateTopResultsResult
)['results'][number];

export interface AeroCandidateExitDetail {
    label: string;
    value: string;
}

export interface AeroCandidateComparisonSummary {
    promotableCandidateCount: number;
    selectedBestAvgProfit: number | null;
    selectedBestScore: number | null;
    selectedCount: number;
    visibleBestAvgProfit: number | null;
    visibleBestScore: number | null;
    visibleCount: number;
}

const MAX_STRUCTURE_PREVIEW_LENGTH = 220;
const MAX_RULE_PREVIEW_LENGTH = 160;

export const buildAeroCandidateExitDetails = (candidate: AeroCandidateReviewRow): AeroCandidateExitDetail[] => [
    {
        label: 'Risk',
        value: formatAeroPercent(candidate.risk)
    },
    {
        label: 'Target',
        value: formatAeroPercent(candidate.target)
    },
    {
        label: 'Trail',
        value: formatAeroPercent(candidate.trail)
    },
    {
        label: 'Profit Rules',
        value: summarizeAeroCandidateText(candidate.profitTakingRules, MAX_RULE_PREVIEW_LENGTH)
    },
    {
        label: 'Structure',
        value: summarizeAeroCandidateText(candidate.exitLogicStructure, MAX_STRUCTURE_PREVIEW_LENGTH)
    }
];

export const buildAeroCandidateComparisonSummary = ({
    results,
    selectedHashes
}: {
    results: AeroCandidateReviewRow[];
    selectedHashes: string[];
}): AeroCandidateComparisonSummary => {
    const selectedHashSet = new Set(selectedHashes);
    const selectedResults = results.filter((candidate) => selectedHashSet.has(candidate.variantHash));

    return {
        promotableCandidateCount: results.filter(isAeroCandidatePromotable).length,
        selectedBestAvgProfit: getBestNumericValue(selectedResults, (candidate) => candidate.avgProfit),
        selectedBestScore: getBestNumericValue(selectedResults, (candidate) => candidate.liveScore),
        selectedCount: selectedResults.length,
        visibleBestAvgProfit: getBestNumericValue(results, (candidate) => candidate.avgProfit),
        visibleBestScore: getBestNumericValue(results, (candidate) => candidate.liveScore),
        visibleCount: results.length
    };
};

export const summarizeAeroCandidateText = (value: string | null | undefined, maxLength: number) => {
    const normalizedValue = normalizeAeroCandidateText(value);

    if (!normalizedValue) return 'None';
    if (normalizedValue.length <= maxLength) return normalizedValue;

    return `${normalizedValue.slice(0, Math.max(0, maxLength - 3))}...`;
};

export const formatAeroPercent = (value: number | null | undefined) => {
    if (value == null || Number.isNaN(value)) return 'None';

    return `${formatAeroNumber(value)}%`;
};

export const normalizeAeroWinRatePercent = (value: number | null | undefined) => {
    if (value == null || Number.isNaN(value)) return null;

    return value <= 1 ? value * 100 : value;
};

export const formatAeroWinRatePercent = (value: number | null | undefined) => {
    const normalizedValue = normalizeAeroWinRatePercent(value);

    if (normalizedValue === null) return 'None';

    return `${formatAeroNumber(normalizedValue)}%`;
};

export const formatAeroNumber = (value: number | null | undefined) => {
    if (value == null || Number.isNaN(value)) return 'None';

    return Number.isInteger(value) ? String(value) : value.toFixed(2);
};

const normalizeAeroCandidateText = (value: string | null | undefined) => {
    const trimmedValue = value?.trim() ?? '';

    if (!trimmedValue) return '';

    return stringifyParsedJson(trimmedValue) ?? trimmedValue.replace(/\s+/g, ' ');
};

const stringifyParsedJson = (value: string) => {
    if (!value.startsWith('{') && !value.startsWith('[')) return null;

    try {
        return JSON.stringify(JSON.parse(value)) ?? null;
    } catch {
        return null;
    }
};

const getBestNumericValue = (results: AeroCandidateReviewRow[], readValue: (candidate: AeroCandidateReviewRow) => number | null) => {
    const values = results.map(readValue).filter((value): value is number => value !== null && Number.isFinite(value));

    return values.length > 0 ? Math.max(...values) : null;
};
